/**
 * SwipaWordmark — "sWipa" с сердечком над W.
 *
 * W собрано из двух V разного цвета:
 *   левая V — base (чёрная),
 *   правая V — accent (ruby).
 * Это читается как "VIP": V + IP. Менять на одну литеру W нельзя — теряется
 * смысл бренда.  2026-04-22: предыдущая версия имела сердечко, съезжавшее
 * не в центр W (баг был в том, что left:50% родительского span'а не
 * совпадает с оптическим центром склеенных V). Фикс: оборачиваем обе V в
 * отдельный wrapper и центрируем сердечко относительно визуальной ширины,
 * а не layout-ширины (которая короче из-за negative margin).
 */
import { CSSProperties } from 'react';

export function SwipaWordmark({
  size = 48,
  base = '#0A0A0A',
  accent = '#C5243A', // ruby — VIP вторая V
  style,
}: {
  size?: number;
  base?: string;
  accent?: string;
  style?: CSSProperties;
}) {
  const font = '"Quicksand","Nunito",system-ui,sans-serif';
  const letterCommon: CSSProperties = {
    fontFamily: font,
    fontWeight: 700,
    fontSize: size,
    letterSpacing: '-0.04em',
    lineHeight: 1,
    display: 'inline-block',
  };
  const letterBase: CSSProperties = { ...letterCommon, color: base };
  const letterAccent: CSSProperties = { ...letterCommon, color: accent };

  // Керн между двумя V: насколько вторая V наезжает на первую, чтобы выглядело как W.
  const kern = size * 0.33;
  const heartSize = Math.round(size * 0.22);

  // Оптический центр W относительно width родительского span:
  //   ширина контейнера = sizeV + (sizeV - kern) = 2*sizeV - kern
  //   визуальный центр  = sizeV/2 (первая V) ... (2*sizeV - kern) - sizeV/2 (вторая V)
  // Середина между ними ≈ (2*sizeV - kern) / 2. Значит left:50% попадает туда,
  // КУДА нам нужно — если считать sizeV ≈ размер em-квадрата. На практике глиф V
  // уже em-квадрата, поэтому подкручиваем маленький offset. Держим строго 50%
  // и не используем хак: сердечко сидит ровно над стыком V+V.

  return (
    <span
      style={{ display: 'inline-flex', alignItems: 'baseline', ...style }}
      aria-label="swipa"
    >
      <span style={letterBase}>s</span>
      <span
        style={{
          position: 'relative',
          display: 'inline-block',
          lineHeight: 1,
        }}
      >
        <span style={letterBase}>V</span>
        <span style={{ ...letterAccent, marginLeft: -kern }}>V</span>
        <svg
          width={heartSize}
          height={heartSize}
          viewBox="0 0 24 24"
          fill={accent}
          style={{
            position: 'absolute',
            left: '50%',
            top: -heartSize * 0.55,
            transform: 'translateX(-50%)',
            pointerEvents: 'none',
            filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.2))',
          }}
          aria-hidden="true"
        >
          <path d="M12 21s-7.5-4.5-9.5-10C1.5 7.5 4 4 7.5 4c2 0 3.5 1 4.5 2.5C13 5 14.5 4 16.5 4 20 4 22.5 7.5 21.5 11c-2 5.5-9.5 10-9.5 10z" />
        </svg>
      </span>
      <span style={letterBase}>ipa</span>
    </span>
  );
}

export default SwipaWordmark;
